import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import './InlineStatusSelect.css';

const getContrastText = (hex) => {
  if (!hex) return '#374151';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#1a1a1a' : '#ffffff';
};

/**
 * Dropdown inline pour changer un statut directement depuis le tableau.
 * Props :
 *   value    — valeur actuelle
 *   options  — [{ value, label, color? }]
 *   onChange — (newValue) => void  (appelé uniquement si valeur change)
 *   disabled — bool (optionnel)
 */
export default function InlineStatusSelect({ value, options, onChange, disabled = false, searchable = false, fullColor = false }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef();
  const dropRef = useRef();
  const searchRef = useRef();

  // Fermer si clic en dehors du trigger ET du dropdown
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inDrop    = dropRef.current   && dropRef.current.contains(e.target);
      if (!inTrigger && !inDrop) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (!open) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left });
      setSearch('');
      setFocusedIndex(-1);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    setOpen(o => !o);
  };

  // Repositionner vers le haut AVANT le paint si ça déborde en bas (pas de flash)
  useLayoutEffect(() => {
    if (!open || !dropRef.current) return;
    const dropRect = dropRef.current.getBoundingClientRect();
    if (dropRect.bottom > window.innerHeight - 8) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: Math.max(8, triggerRect.top - dropRect.height - 4), left: triggerRect.left });
    }
  }, [open]);

  const filteredOptions = options.filter(opt => !search || opt.label.toLowerCase().includes(search.toLowerCase()));

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
        const opt = filteredOptions[focusedIndex];
        if (String(opt.value) !== String(value)) onChange(opt.value);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const current = options.find(o => String(o.value) === String(value)) || options[0];
  if (!current) return null;

  return (
    <div className="iss-wrap">
      <button
        ref={triggerRef}
        className="iss-trigger"
        style={current.color ? (fullColor ? {
          background:  current.color,
          color:       getContrastText(current.color),
          borderColor: current.color,
        } : {
          background:  current.color + '20',
          color:       current.color,
          borderColor: current.color + '60',
        }) : {}}
        onClick={handleToggle}
        disabled={disabled}
        title="Cliquer pour changer le statut"
      >
        {current.label}
        {!disabled && <span className="iss-chevron">▾</span>}
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="iss-dropdown"
          style={{ position: 'fixed', top: dropPos.top, left: dropPos.left }}
          onKeyDown={handleKeyDown}
        >
          {searchable && (
            <input
              ref={searchRef}
              type="text"
              className="iss-search"
              placeholder="Rechercher..."
              value={search}
              onChange={e => { setSearch(e.target.value); setFocusedIndex(-1); }}
              onClick={e => e.stopPropagation()}
              onKeyDown={handleKeyDown}
            />
          )}
          {filteredOptions.map((opt, idx) => {
            const isActive = String(opt.value) === String(value);
            const isFocused = idx === focusedIndex;
            const fullBg = fullColor && opt.color;
            return (
              <button
                key={opt.value}
                className={`iss-option ${isActive ? 'active' : ''} ${isFocused ? 'focused' : ''}${fullBg ? ' iss-full-color' : ''}`}
                style={fullBg ? { background: opt.color, color: getContrastText(opt.color) } : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isActive) onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.color && !fullColor && <span className="iss-dot" style={{ background: opt.color }} />}
                {opt.label}
                {isActive && fullBg && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
